import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { User } from '../model/user';
import { environment } from '../../environments/environment';
import { Observable, ReplaySubject } from 'rxjs';

export interface VerifyAddressResponse {
  status: string;
  message: string;
  data?: User;
  code?: number;
}

@Injectable({
  providedIn: 'root',
})
export class UserService {
  constructor(private http: HttpClient) {}
  private userSubject: ReplaySubject<any> = new ReplaySubject<any>(1);

  // The session cookie and CSRF header come from authInterceptor, as for every
  // other call here.
  verifyAddress(): Observable<VerifyAddressResponse> {
    return this.http.post<VerifyAddressResponse>(
      environment.apiUrl + '/user/location/verify',
      null,
      { responseType: 'json' }
    );
  }

  updateUser(user: User) {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    return this.http.put<any>(
      environment.apiUrl + '/user',
      JSON.stringify(user),
      { headers, responseType: 'json' }
    );
  }

  updateDarkMode(enabled: boolean) {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    return this.http.put<any>(
      environment.apiUrl + '/user/dark-mode',
      JSON.stringify({ darkModeEnabled: enabled }),
      { headers, responseType: 'json' }
    );
  }

  updateUserEmail(user: User) {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    return this.http.put<any>(
      environment.apiUrl + '/user/email',
      JSON.stringify(user),
      { headers, responseType: 'json' }
    );
  }

  updateServiceCapability(deleted: boolean, user: User, serviceType: string) {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    const params = { deleted: deleted, serviceType: serviceType };
    return this.http.post<any>(
      environment.apiUrl + '/user/service-type',
      JSON.stringify(user),
      {
        headers,
        responseType: 'json',
        params: params,
      }
    );
  }

  updateServiceSubType(
    deleted: boolean,
    user: User,
    serviceType: string,
    serviceSubType: any
  ) {
    const params = {
      deleted: deleted,
      serviceTypeIdentifier: serviceType,
    };
    const formData = new FormData();
    formData.set(
      'user',
      new Blob([JSON.stringify(user)], {
        type: 'application/json',
      })
    );
    formData.set(
      'serviceSubType',
      new Blob([JSON.stringify(serviceSubType)], {
        type: 'application/json',
      })
    );
    return this.http.post<any>(
      environment.apiUrl + '/user/service-sub-type',
      formData,
      {
        responseType: 'json',
        params: params,
      }
    );
  }

  addNewServiceZone(user: User, serviceZone: any) {
    const formData = new FormData();
    formData.set(
      'user',
      new Blob([JSON.stringify(user)], {
        type: 'application/json',
      })
    );
    formData.set(
      'serviceZone',
      new Blob([JSON.stringify(serviceZone)], {
        type: 'application/json',
      })
    );
    return this.http.post<any>(
      environment.apiUrl + '/user/service-zone',
      formData,
      {
        responseType: 'json',
      }
    );
  }

  updateServiceZone(
    user: User,
    serviceZone: any,
    serviceZoneIdentifier: string
  ) {
    const formData = new FormData();
    formData.set(
      'user',
      new Blob([JSON.stringify(user)], {
        type: 'application/json',
      })
    );
    formData.set(
      'serviceZone',
      new Blob([JSON.stringify(serviceZone)], {
        type: 'application/json',
      })
    );
    formData.set('serviceZoneIdentifier', serviceZoneIdentifier);
    return this.http.put<any>(
      environment.apiUrl + '/user/service-zone',
      formData,
      {
        responseType: 'json',
      }
    );
  }

  removeServiceZone(userId: string, serviceZoneIdentifier: string) {
    let params = new HttpParams()
      .set('userId', userId)
      .set('serviceZoneIdentifier', serviceZoneIdentifier);
    return this.http.delete<any>(environment.apiUrl + '/user/service-zone', {
      responseType: 'json',
      params: params,
    });
  }

  public updateUserOutOfOfficeDates(user: User): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this.http.put<any>(
      environment.apiUrl + `/user/out-of-office-dates`,
      JSON.stringify(user),
      {
        headers,
        responseType: 'json',
      }
    );
  }

  public sendContactUsEmail(contactUsRequest: any) {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this.http.post<any>(
      environment.apiUrl + `/user/contact-us`,
      JSON.stringify(contactUsRequest),
      {
        headers,
        responseType: 'json',
      }
    );
  }

  public optInUserToSmsNotification(user: User) {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this.http.post<any>(
      environment.apiUrl + '/sms/optIn',
      JSON.stringify(user),
      {
        headers,
        responseType: 'json',
      }
    );
  }

  public optOutUserFromSmsNotification(user: User) {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this.http.post<any>(
      environment.apiUrl + '/sms/optOut',
      JSON.stringify(user),
      {
        headers,
        responseType: 'json',
      }
    );
  }

  // be careful when using this, usually want to get the user stored in local storage
  // only doing this to pass data from my account component to the header component
  public getUser() {
    return this.userSubject.asObservable();
  }

  public updateUserSubject(user: User) {
    this.userSubject.next(user);
  }
}
